import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import { ChevronRight, X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { ApiError, ApiErrorResponseSchema, importEndpoints } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import {
  SelectedImportFileCard,
  TemplateDownloadHint,
  UploadDropzoneCard,
} from './upload-phase-sections';
import { ACCEPTED_PHOTO_ACCEPT } from '../lib/upload-file-selection';
import { PhotoSupplierPicker } from './photo-supplier-picker';
import { useUploadMutations } from './use-upload-mutations';
import type { UploadPhaseOnNextArgs } from './use-upload-mutations';
import { useUploadSelection } from '../hooks/use-upload-selection';

export type { UploadPhaseOnNextArgs };

interface UploadPhaseProps {
  onNext: (args: UploadPhaseOnNextArgs) => void;
}

/** Shared className for the OOH/DOOH template-download link buttons. */
const TEMPLATE_LINK_CLASS_NAME = 'h-auto p-0 text-[13px] font-medium';

function getTemplateStatusLabel(file: File, label: string): string | undefined {
  const normalizedName = file.name.toLowerCase();
  if (
    normalizedName.endsWith('.xlsx') ||
    normalizedName.endsWith('.xls') ||
    normalizedName.endsWith('.xlsm')
  ) {
    return label;
  }
  return undefined;
}

/** Server-provided message from a structured API error body; undefined for any other failure. */
function templateDownloadMessage(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const parsed = ApiErrorResponseSchema.safeParse(err.body);
  return parsed.success ? parsed.data.message : undefined;
}

// eslint-disable-next-line complexity -- two distinct file-type flows (XLSX/ZIP) with validation paths; mutations/selection state extracted to hooks
export function UploadPhase({ onNext }: UploadPhaseProps) {
  const { t } = useTranslation('mediaImport');
  const api = useApiClient();
  const sel = useUploadSelection();

  const { uploadMutation, photoBundleMutation } = useUploadMutations(
    api,
    t('upload.errorGeneric'),
    onNext,
    (msg) => sel.setServerError(msg),
  );

  function handleUpload() {
    if (sel.isPhotoBatch && sel.selectedSupplierId !== null) {
      photoBundleMutation.mutate({
        file: sel.selectedPhotoFiles,
        supplierId: sel.selectedSupplierId,
      });
      return;
    }
    if (sel.selectedFile === null) return;
    if (sel.isZip && sel.selectedSupplierId !== null) {
      photoBundleMutation.mutate({ file: sel.selectedFile, supplierId: sel.selectedSupplierId });
    } else if (!sel.isZip) {
      uploadMutation.mutate(sel.selectedFile);
    }
  }

  async function handleTemplateDownload(type: 'ooh' | 'dooh' = 'ooh'): Promise<void> {
    const a = document.createElement('a');
    a.download = type === 'dooh' ? 'szablon_DOOH.xlsx' : 'szablon_OOH.xlsx';
    document.body.appendChild(a);
    try {
      const blob = await api.download(importEndpoints.getTemplate, { type });
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[template-download]', err);
      toast.error(templateDownloadMessage(err) ?? t('upload.errorTemplateDownload'));
    } finally {
      document.body.removeChild(a);
    }
  }

  const error = sel.clientError ?? sel.serverError;
  const isMutationPending = uploadMutation.isPending || photoBundleMutation.isPending;
  const isUploadDisabled =
    !sel.hasSelection || isMutationPending || (sel.isPhotoMode && sel.selectedSupplierId === null);

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="phase-upload">
      <UploadDropzoneCard
        title={t('upload.dropZoneTitle')}
        subtitle={t('upload.dropZoneSubtitle')}
        isDragging={sel.isDragging}
        ariaLabel={t('upload.dropZoneTitle')}
        onDragOver={(e) => {
          e.preventDefault();
          sel.setIsDragging(true);
        }}
        onDragLeave={() => sel.setIsDragging(false)}
        onDrop={sel.handleDrop}
        onClick={() => sel.fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') sel.fileInputRef.current?.click();
        }}
        selectFileButton={
          <>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                sel.fileInputRef.current?.click();
              }}
            >
              {t('upload.selectFile')}
            </Button>
            <Input
              ref={sel.fileInputRef}
              type="file"
              multiple
              accept={`.xlsx,.xls,.xlsm,.csv,.zip,application/zip,application/x-zip-compressed,${ACCEPTED_PHOTO_ACCEPT}`}
              className="hidden"
              data-testid="file-input"
              onChange={(e) => {
                sel.handleFilesSelected(Array.from(e.target.files ?? []));
                // Reset the value so re-selecting a file with the SAME name still fires
                // onChange (browsers suppress it when the input value is unchanged) — CU-86ca81670.
                e.target.value = '';
              }}
            />
          </>
        }
      />

      {error !== null ? (
        <div
          className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-700"
          data-testid="upload-error"
        >
          {error}
        </div>
      ) : null}

      {sel.selectedFile !== null ? (
        <SelectedImportFileCard
          fileName={sel.selectedFile.name}
          metaText={t('upload.filePreviewSize', {
            sizeKb: Math.round(sel.selectedFile.size / 1024),
          })}
          statusText={getTemplateStatusLabel(sel.selectedFile, t('upload.validTemplateStatus'))}
          removeButton={
            <Button
              variant="ghost"
              size="icon-sm"
              type="button"
              aria-label={t('upload.removeFile')}
              onClick={() => {
                sel.setSelectedFile(null);
                sel.clearSelectionErrors();
              }}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          }
        />
      ) : sel.isPhotoBatch ? (
        <SelectedImportFileCard
          fileName={t('upload.photosSelectedLabel', { count: sel.selectedPhotoFiles.length })}
          metaText={t('upload.filePreviewSize', {
            sizeKb: Math.round(sel.selectedPhotoFiles.reduce((sum, f) => sum + f.size, 0) / 1024),
          })}
          removeButton={
            <Button
              variant="ghost"
              size="icon-sm"
              type="button"
              aria-label={t('upload.removeFile')}
              onClick={() => {
                sel.setSelectedPhotoFiles([]);
                sel.clearSelectionErrors();
              }}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          }
        />
      ) : null}

      {sel.isPhotoMode ? (
        <div className="space-y-2">
          <PhotoSupplierPicker
            value={sel.selectedSupplierId}
            onChange={sel.setSelectedSupplierId}
          />
        </div>
      ) : null}

      {!sel.isPhotoMode ? (
        <TemplateDownloadHint
          prefixText={t('upload.noFile')}
          downloadLink={
            <span className="flex items-center gap-3">
              <Button
                type="button"
                variant="link"
                className={TEMPLATE_LINK_CLASS_NAME}
                data-testid="download-template-ooh"
                onClick={() => void handleTemplateDownload('ooh')}
              >
                {t('upload.downloadTemplateOoh')}
              </Button>
              <Button
                type="button"
                variant="link"
                className={TEMPLATE_LINK_CLASS_NAME}
                data-testid="download-template-dooh"
                onClick={() => void handleTemplateDownload('dooh')}
              >
                {t('upload.downloadTemplateDooh')}
              </Button>
            </span>
          }
        />
      ) : null}

      <div className="flex justify-end pt-2">
        {sel.isPhotoMode ? (
          <Button
            type="button"
            data-testid="zip-upload-button"
            onClick={handleUpload}
            disabled={isUploadDisabled}
          >
            {isMutationPending ? t('upload.uploading') : t('upload.uploadPhotosButton')}
            {isMutationPending ? null : <ChevronRight className="h-4 w-4" />}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleUpload}
            disabled={isUploadDisabled}
            data-testid="next-to-validation"
          >
            {isMutationPending ? t('upload.uploading') : t('upload.nextButton')}
            {isMutationPending ? null : <ChevronRight className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
}
